import {
  collectionGroup,
  getDocs,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * マスターアンケートテンプレの取込状況集計 (AW8、docs/108 Medium)。
 *
 * 旧2 `lib/surveys.ts::getMasterSurveyUsageCounts` 踏襲: collectionGroup('surveys') で地区配下の
 * アンケートを全件読み、sourceMasterSurveyId 別に linked / forked 件数を集計する。
 * v2 では master は `surveyPresets` collection なので preset.id をキーに地区 survey を逆引きする。
 *
 * 認可: firestore.rules `match /districts/{districtId}/surveys/{surveyId}` の
 * `allow read: if isSignedIn()` が collectionGroup query を許可する (master / district admin 共に
 * 認証済なら read 可)。master 一覧でのみ呼び出す想定。
 *
 * 失敗時 (index 未作成等) は空 Map を返し、UI は「取込状況不明」扱いにする (致命傷にしない)。
 */

export interface UsageCount {
  linked: number;
  forked: number;
}

export type SurveyUsageState =
  | { status: 'loading'; counts: Map<string, UsageCount> }
  | { status: 'ready'; counts: Map<string, UsageCount> }
  | { status: 'error'; counts: Map<string, UsageCount>; error: Error };

/** snapshot から sourceMasterSurveyId 別の linked/forked 件数を集計 (pure、test 容易化のため export)。 */
export function tallyUsageCounts(
  docs: Array<QueryDocumentSnapshot<DocumentData> | { data: () => DocumentData }>,
): Map<string, UsageCount> {
  const counts = new Map<string, UsageCount>();
  for (const d of docs) {
    const data = d.data();
    const sourceId =
      typeof data.sourceMasterSurveyId === 'string' ? data.sourceMasterSurveyId : null;
    if (!sourceId) continue;
    if (!counts.has(sourceId)) counts.set(sourceId, { linked: 0, forked: 0 });
    const entry = counts.get(sourceId)!;
    if (data.linkedToMaster === 'linked') entry.linked++;
    else if (data.linkedToMaster === 'forked') entry.forked++;
  }
  return counts;
}

/**
 * 全地区 survey を collectionGroup で 1 回読み、preset 別取込件数を集計する hook。
 * `enabled=false` (master 以外) のときは query を投げず空のまま loading→ready しない。
 */
export function useSurveyUsageCounts(enabled: boolean): SurveyUsageState {
  const [state, setState] = useState<SurveyUsageState>({
    status: 'loading',
    counts: new Map(),
  });

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'ready', counts: new Map() });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', counts: new Map() });
    // 旧2 同様、全地区 survey を 1 回読み JS 側で sourceMasterSurveyId 別に集計
    // (composite index 不要、取込のない survey は tally 側で skip)。
    getDocs(collectionGroup(db, 'surveys'))
      .then((snap) => {
        if (cancelled) return;
        setState({ status: 'ready', counts: tallyUsageCounts(snap.docs) });
      })
      .catch((error: Error) => {
        if (cancelled) return;
        // index 未作成等は致命傷にしない (空 Map + error 状態)。
        setState({ status: 'error', counts: new Map(), error });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
}
